import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type PickerOption = {
  id: string;
  label: string;
  description?: string;
  keywords?: string[];
};

type FeatureFlagTargetPickerProps = {
  label: string;
  placeholder: string;
  emptyLabel: string;
  options: PickerOption[];
  value: string;
  onSelect: (value: string) => void;
};

export const FeatureFlagTargetPicker = ({
  label,
  placeholder,
  emptyLabel,
  options,
  value,
  onSelect,
}: FeatureFlagTargetPickerProps) => {
  const [open, setOpen] = useState(false);

  const selectedOption = useMemo(() => options.find((option) => option.id === value), [options, value]);
  const triggerCopy = selectedOption?.description ? `${selectedOption.label} — ${selectedOption.description}` : selectedOption?.label ?? placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} aria-label={`${label} target picker`} className="w-full justify-between">
          <span className="truncate text-left">{triggerCopy}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={[option.label, option.description ?? "", option.id, ...(option.keywords ?? [])].join(" ")}
                  onSelect={() => {
                    onSelect(option.id);
                    setOpen(false);
                  }}
                  className="flex items-start gap-2 py-3"
                >
                  <Check className={cn("mt-0.5 h-4 w-4 shrink-0", value === option.id ? "opacity-100" : "opacity-0")} />
                  <div className="min-w-0">
                    <p className="truncate font-medium">{option.label}</p>
                    {option.description && <p className="line-clamp-2 text-xs text-muted-foreground">{option.description}</p>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
